import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@material-ui/core';
import { ArrowRight, Check, Search, Star } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { AnimatedCheckmark } from '../components/AnimatedCheckmark';
import { Headline } from '../components/Headline';
import { Kicker } from '../components/Kicker';
import { LogosSection } from '../components/LogosSection';
import { mobileHeroLeadText } from '../components/MarketingTypography';
import { AnimatedNumber, Reveal } from '../components/motion';
import { SEO } from '../components/SEO';
import ENV from '../env.json';
import MainLayout from '../layouts/main';
import styled from '../styled';
import { COLORS } from '../themes/color';
import { CtaClickLabel, sendCtaClickEvent } from '../tracking';

/* ---------------------------- Accents ----------------------------- */

/**
 * Serif words with a blue "marker" highlight behind the lower half.
 * The launch accent: distinctive without leaving the Affilimate palette.
 */
const Highlight = styled('span')`
  background: linear-gradient(
    180deg,
    transparent 58%,
    rgba(46, 92, 255, 0.18) 58%
  );
  padding: 0 2px;
`;

/**
 * The ScaleVisible wordmark ships as a PNG with a baked-in white background,
 * so it has to multiply against whatever surface it lands on.
 */
const ScaleVisibleMark = styled('img')`
  mix-blend-mode: multiply;
`;

/* --------------------- Partnership masthead ----------------------- */

const Masthead = styled('section')`
  position: relative;
  padding: 120px 24px 0;
  background: linear-gradient(
    180deg,
    #ffffff 0%,
    #ffffff 58%,
    #f7f9fd 80%,
    #f2f5fb 100%
  );
  text-align: center;

  @media (max-width: 800px) {
    padding: 96px 24px 0;
  }
`;

const MastheadInner = styled('div')`
  max-width: 760px;
  margin: 0 auto;
`;

/** Editorial kicker: small caps flanked by hairlines, press-announcement style. */
const RuleKicker = styled('div')`
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: 560px;
  margin: 0 auto 28px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${COLORS.GRAY.muted};

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${COLORS.GRAY.divider};
  }

  span.label {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  @media (max-width: 600px) {
    font-size: 9.5px;
    letter-spacing: 0.1em;
    gap: 10px;
    margin-bottom: 22px;
  }
`;

const BigLockup = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin: 0 auto 24px;

  img {
    height: 28px;
    width: auto;
  }

  @media (max-width: 600px) {
    gap: 12px;
    margin-bottom: 20px;

    img {
      height: 20px;
    }
  }
`;

const LockupTimes = styled('span')`
  font-family: 'Merriweather', serif;
  font-size: 20px;
  line-height: 1;
  color: ${COLORS.GRAY.mutedLight};
  flex-shrink: 0;

  @media (max-width: 600px) {
    font-size: 15px;
  }
`;

const MastheadLead = styled('p')`
  font-size: 15px;
  line-height: 1.6;
  color: ${COLORS.TEXT_SECONDARY};
  max-width: 580px;
  margin: 16px auto 0;

  @media (max-width: 600px) {
    font-size: 14px;
  }
`;

const PartnerColumns = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  text-align: left;
  max-width: 760px;
  margin: 32px auto 0;
  border-top: 1px solid ${COLORS.GRAY.divider};

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    margin-top: 28px;
  }
`;

const PartnerCol = styled('div')`
  padding: 24px 28px 0;

  &:first-of-type {
    padding-left: 4px;
  }

  & + & {
    border-left: 1px solid ${COLORS.GRAY.divider};
    padding-right: 4px;
  }

  p {
    font-size: 14px;
    line-height: 1.6;
    color: ${COLORS.TEXT_SECONDARY};
    margin: 0;
  }

  @media (max-width: 800px) {
    padding: 20px 4px 0;

    & + & {
      border-left: none;
      margin-top: 20px;
      border-top: 1px solid ${COLORS.GRAY.divider};
    }
  }
`;

/** Anchor for the closing line; stitch is out-of-flow so it paints in the bridge. */
const MastheadClosing = styled('div')`
  position: relative;
  z-index: 2;
  margin: 40px auto 0;
  max-width: 560px;

  @media (max-width: 600px) {
    margin-top: 32px;
  }
`;

const ClosingLine = styled('p')`
  position: relative;
  z-index: 1;
  font-family: 'Merriweather', serif;
  font-size: 18px;
  font-weight: 700;
  color: ${COLORS.INK};
  text-align: center;
  margin: 0 auto;
  line-height: 1.45;
  letter-spacing: -0.01em;

  @media (max-width: 600px) {
    font-size: 16px;
  }
`;

/** Vertical hairline + node — overlaid on the bridge, not in masthead layout flow. */
const Stitch = styled('div')`
  position: absolute;
  top: 0;
  left: 50%;
  z-index: 2;
  width: 2px;
  height: 72px;
  transform: translateX(-50%);
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(46, 92, 255, 0) 0%,
    rgba(46, 92, 255, 0.45) 100%
  );

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 50%;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid ${COLORS.PRIMARY};
    transform: translateX(-50%);
  }

  @media (max-width: 800px) {
    height: 52px;
  }
`;

/**
 * Dedicated white-to-ink fade between the light masthead and the dark
 * exchange floor. Sits flush against both sections — no gap, no restart.
 */
const BRIDGE_NOISE = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg"><filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.72" numOctaves="4" stitchTiles="stitch"/></filter><rect width="100%" height="100%" filter="url(#n)"/></svg>'
)}")`;

const MastheadBridge = styled('section')`
  position: relative;
  height: 280px;
  background: linear-gradient(
    180deg,
    #f2f5fb 0%,
    #dce6f5 16%,
    #b8c9e8 30%,
    #94aed8 42%,
    #7289b8 54%,
    #556a96 66%,
    #3a4a68 78%,
    #1a2438 90%,
    #060d14 100%
  );

  @media (max-width: 800px) {
    height: 210px;
  }
`;

const BridgeBlobs = styled('div')`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
      ellipse 44% 58% at 22% 52%,
      rgba(120, 190, 255, 0.22) 0%,
      rgba(120, 190, 255, 0) 72%
    ),
    radial-gradient(
      ellipse 38% 52% at 78% 48%,
      rgba(160, 140, 255, 0.16) 0%,
      rgba(160, 140, 255, 0) 70%
    ),
    radial-gradient(
      ellipse 48% 62% at 50% 64%,
      rgba(46, 92, 255, 0.12) 0%,
      rgba(46, 92, 255, 0) 74%
    );
`;

/** Fine grain + faint horizontal rules — ticker-board texture. */
const BridgeGrain = styled('div')`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.22;
  mix-blend-mode: soft-light;
  background-image: ${BRIDGE_NOISE},
    repeating-linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.05) 0px,
      rgba(255, 255, 255, 0.05) 1px,
      transparent 1px,
      transparent 5px
    );
  background-size: 160px 160px, auto;
  mask-image: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.15) 0%,
    rgba(0, 0, 0, 0.55) 30%,
    rgba(0, 0, 0, 0.7) 60%,
    rgba(0, 0, 0, 0.35) 100%
  );
  -webkit-mask-image: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.15) 0%,
    rgba(0, 0, 0, 0.55) 30%,
    rgba(0, 0, 0, 0.7) 60%,
    rgba(0, 0, 0, 0.35) 100%
  );
`;

/* ----------------------- The exchange floor ------------------------ */

/**
 * Dark "trading floor" for the launch hero. Fades in from the white
 * masthead through slate blues into ink, NYSE board style.
 */
const DarkFloor = styled('section')`
  position: relative;
  overflow: hidden;
  background: #060d14;
  padding-bottom: 110px;

  @media (max-width: 800px) {
    padding-bottom: 80px;
  }
`;

/** Subtle depth on an already-dark floor — no white restarts. */
const FloorBackdrop = styled('div')`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(180deg, #060d14 0%, #080f18 30%, #040810 100%);
`;

/** AI color blobs, masked so they bloom in the dark body not the bridge. */
const FloorColorBlobs = styled('div')`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
      ellipse 34% 42% at 12% 28%,
      rgba(46, 92, 255, 0.2) 0%,
      rgba(46, 92, 255, 0) 72%
    ),
    radial-gradient(
      ellipse 30% 46% at 88% 22%,
      rgba(124, 92, 255, 0.16) 0%,
      rgba(124, 92, 255, 0) 70%
    ),
    radial-gradient(
      ellipse 36% 48% at 52% 38%,
      rgba(46, 92, 255, 0.14) 0%,
      rgba(46, 92, 255, 0) 68%
    ),
    radial-gradient(
      ellipse 40% 50% at 50% 82%,
      rgba(46, 92, 255, 0.1) 0%,
      rgba(46, 92, 255, 0) 70%
    );
  mask-image: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.15) 0%,
    rgba(0, 0, 0, 0.55) 18%,
    rgba(0, 0, 0, 1) 40%,
    rgba(0, 0, 0, 1) 100%
  );
  -webkit-mask-image: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.15) 0%,
    rgba(0, 0, 0, 0.55) 18%,
    rgba(0, 0, 0, 1) 40%,
    rgba(0, 0, 0, 1) 100%
  );

  @keyframes floorBlobDrift {
    0% {
      transform: translate(0, 0) scale(1);
    }
    100% {
      transform: translate(10px, -6px) scale(1.03);
    }
  }

  animation: floorBlobDrift 22s ease-in-out infinite alternate;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const FloorGlow = styled('div')`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
      ellipse 55% 45% at 50% 12%,
      rgba(46, 92, 255, 0.12) 0%,
      rgba(46, 92, 255, 0) 70%
    ),
    radial-gradient(
      ellipse 45% 55% at 8% 75%,
      rgba(46, 92, 255, 0.08) 0%,
      rgba(46, 92, 255, 0) 70%
    ),
    radial-gradient(
      ellipse 38% 45% at 92% 55%,
      rgba(124, 92, 255, 0.07) 0%,
      rgba(124, 92, 255, 0) 70%
    );
`;

const FloorDots = styled('div')`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.07) 1px,
    transparent 1px
  );
  background-size: 26px 26px;
  mask-image: radial-gradient(
    ellipse 80% 70% at 50% 40%,
    rgba(0, 0, 0, 0.45) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  -webkit-mask-image: radial-gradient(
    ellipse 80% 70% at 50% 40%,
    rgba(0, 0, 0, 0.45) 0%,
    rgba(0, 0, 0, 0) 100%
  );
`;

const FloorInner = styled('div')`
  position: relative;
  z-index: 1;
  max-width: 880px;
  margin: 0 auto;
  padding: 56px 24px 0;
  text-align: center;

  @media (max-width: 800px) {
    padding-top: 44px;
  }
`;

const FloorKicker = styled('div')`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #7fa0ff;
  margin-bottom: 18px;
`;

/** Blue marker highlight tuned for white serif text on the dark floor. */
const HighlightDark = styled('span')`
  background: linear-gradient(
    180deg,
    transparent 58%,
    rgba(46, 92, 255, 0.55) 58%
  );
  padding: 0 2px;
`;

const BlueButton = styled('button')`
  background-color: ${COLORS.PRIMARY};
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  border: none;
  border-radius: 100px;
  padding: 15px 34px;
  cursor: pointer;
  box-shadow: 0 10px 34px rgba(46, 92, 255, 0.45);
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    background-color: #2249d8;
    transform: translateY(-2px);
    box-shadow: 0 14px 40px rgba(46, 92, 255, 0.5);
  }
`;

/* -------------------------- Ticker tapes --------------------------- */

type TickerItem = {
  label: string;
  src?: string;
  delta?: string;
  down?: boolean;
};

const TAPE_KEYWORDS: TickerItem[] = [
  { label: 'best espresso machine', delta: '▲ 212 citations' },
  { label: '"quiet coffee grinder"', delta: '▲ 3 new answers' },
  { label: 'pour-over kettle', delta: '▲ 18% AI visibility' },
  { label: 'decaf espresso beans', delta: '▼ 4%', down: true },
  { label: 'single-origin subscription', delta: '▲ 9% citations' },
  { label: 'moka pot vs aeropress', delta: '▲ 41 prompts/day' },
  { label: 'best latte machine under $500', delta: '▲ 27 citations' }
];

const TAPE_PLATFORMS: TickerItem[] = [
  { label: 'ChatGPT cited', src: '/best-espresso-machines' },
  { label: 'Perplexity crawl', src: '/burr-grinder-guide', delta: '4,820/mo' },
  { label: 'Google AI Overviews cited', src: '/coffee-subscriptions-tested' },
  { label: 'Gemini cited', src: '/french-press-buying-guide' },
  { label: 'Claude crawl', src: '/espresso-accessories', delta: '▲ 38%' },
  { label: 'ChatGPT cited', src: '/best-coffee-beans-2026' }
];

const TAPE_PUBLISHERS: TickerItem[] = [
  { label: 'homebarista.com', delta: '▲ new sponsorship live' },
  { label: 'thecoffeecritic.com/reviews', delta: '2,447 agent visits/mo' },
  { label: 'dailygrind.co', delta: '▲ campaign renewed' },
  { label: 'brewsetter.com/gear', delta: '▲ 12% citations' },
  { label: 'espressolab.io', delta: 'baseline set' },
  { label: 'roastreport.com', delta: '▲ 2 packages listed' }
];

const TickerZone = styled('div')`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 72px;

  @media (max-width: 800px) {
    margin-top: 52px;
  }
`;

const TapeViewport = styled('div')<{ $dim?: number }>`
  overflow: hidden;
  width: 100%;
  opacity: ${(p) => p.$dim ?? 1};
  mask-image: linear-gradient(
    90deg,
    transparent 0%,
    #000 8%,
    #000 92%,
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    90deg,
    transparent 0%,
    #000 8%,
    #000 92%,
    transparent 100%
  );
`;

const TickerTrack = styled('div')<{ $duration: number; $reverse?: boolean }>`
  display: flex;
  gap: 14px;
  width: fit-content;
  padding-right: 14px;

  @keyframes tapeScroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-50%);
    }
  }

  animation: tapeScroll ${(p) => p.$duration}s linear infinite;
  animation-direction: ${(p) => (p.$reverse ? 'reverse' : 'normal')};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const TickerChip = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  white-space: nowrap;
  font-family: 'SFMono-Regular', 'Roboto Mono', Menlo, Consolas, monospace;
  font-size: 12.5px;
  color: #aebcd4;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 8px;
  padding: 9px 15px;

  .src {
    color: #7f99c4;
  }

  .up {
    color: #4ade80;
  }

  .down {
    color: #f87171;
  }
`;

const Tape = ({
  items,
  duration,
  reverse,
  dim
}: {
  items: TickerItem[];
  duration: number;
  reverse?: boolean;
  dim?: number;
}) => {
  const doubled = [...items, ...items];

  return (
    <TapeViewport $dim={dim}>
      <TickerTrack $duration={duration} $reverse={reverse}>
        {doubled.map((item, index) => (
          <TickerChip key={index}>
            {item.label}
            {item.src && <span className="src">{item.src}</span>}
            {item.delta && (
              <span className={item.down ? 'down' : 'up'}>{item.delta}</span>
            )}
          </TickerChip>
        ))}
      </TickerTrack>
    </TapeViewport>
  );
};

const HeroLead = styled('p')`
  font-size: 20px;
  line-height: 1.6;
  color: #b6c2d4;
  max-width: 720px;
  margin: 28px auto 40px;
  ${mobileHeroLeadText}
`;

const ButtonRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  flex-wrap: wrap;
`;

const InkButton = styled('button')`
  background-color: ${COLORS.INK};
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  border: none;
  border-radius: 100px;
  padding: 15px 34px;
  cursor: pointer;
  box-shadow: 0 8px 26px rgba(14, 28, 43, 0.28);
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    background-color: #1f3348;
    transform: translateY(-2px);
    box-shadow: 0 12px 34px rgba(14, 28, 43, 0.32);
  }
`;

const OutlineButton = styled('button')`
  background-color: #fff;
  color: ${COLORS.INK};
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  border: 1.5px solid ${COLORS.GRAY.divider};
  border-radius: 100px;
  padding: 14px 32px;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    color: ${COLORS.PRIMARY};
    transform: translateY(-2px);
  }
`;

const SecondaryAnchor = styled('a')<{ $onDark?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${(p) => (p.$onDark ? '#93b0ff' : COLORS.PRIMARY)};
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }
`;

/* ----------------------- AI answer mockup ------------------------- */

const MockupWrapper = styled('div')`
  position: relative;
  z-index: 2;
  max-width: 720px;
  margin: -26px auto 0;
  padding: 0 24px;

  @media (max-width: 800px) {
    margin-top: -20px;
  }
`;

const AnswerCard = styled('div')`
  background: #1a2438;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.55);
  text-align: left;
  overflow: hidden;
`;

const AnswerHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 13px;
  font-weight: 600;
  color: #c8d4e8;

  img.g-logo {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

const AnswerBody = styled('div')`
  padding: 20px;
`;

const PromptRow = styled('div')`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`;

const UserPrompt = styled('div')`
  display: inline-block;
  max-width: 88%;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 14px 14px 4px 14px;
  padding: 10px 16px;
  font-size: 14.5px;
  color: #f4f7fc;
  text-align: left;
`;

const PickCard = styled('div')`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 18px;
`;

const PickBadge = styled('div')`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #93b0ff;
  margin-bottom: 10px;
`;

const PickTitle = styled('div')`
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 8px;
  letter-spacing: -0.01em;
`;

const PickMeta = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  font-size: 13px;
  color: #9aabc4;
  margin-bottom: 12px;

  .price {
    font-weight: 700;
    color: #dbe6ff;
  }

  .dot {
    opacity: 0.45;
  }

  .rating {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #c8d4e8;
  }

  .rating svg {
    color: #fbbf24;
  }
`;

const PickDescription = styled('p')`
  font-size: 14px;
  line-height: 1.65;
  color: #b8c4d8;
  margin: 0;

  strong {
    color: #fff;
  }
`;

const SourcesLabel = styled('div')`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #7f8fa8;
  margin-bottom: 10px;
`;

const SourceRow = styled('div')`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const SourceChip = styled('div')<{ $highlight?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  font-size: 12.5px;
  font-weight: 500;
  color: ${(p) => (p.$highlight ? '#dbe6ff' : '#9aabc4')};
  background: ${(p) =>
    p.$highlight ? 'rgba(46, 92, 255, 0.12)' : 'rgba(255, 255, 255, 0.04)'};
  border: 1px solid
    ${(p) =>
      p.$highlight ? 'rgba(46, 92, 255, 0.35)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 100px;
  padding: 5px 12px 5px 5px;
  box-shadow: ${(p) =>
    p.$highlight ? '0 4px 18px rgba(46, 92, 255, 0.15)' : 'none'};

  .domain {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SourceIcon = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: #fff;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 16px;
    height: 16px;
    object-fit: contain;
    display: block;
  }
`;

const YourPageTag = styled('span')`
  flex-shrink: 0;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #fff;
  background: ${COLORS.PRIMARY};
  border-radius: 100px;
  padding: 3px 8px;
`;

const AiAnswerMockup = () => (
  <MockupWrapper aria-hidden="true">
    <Reveal delay={150}>
      <AnswerCard>
        <AnswerHeader>
          <img
            className="g-logo"
            src="/images/ai-exchange/google-g.svg"
            alt=""
          />
          Google AI Mode
        </AnswerHeader>
        <AnswerBody>
          <PromptRow>
            <UserPrompt>
              What&apos;s the best espresso machine under $300?
            </UserPrompt>
          </PromptRow>
          <PickCard>
            <PickBadge>The Best Pure Budget Choice</PickBadge>
            <PickTitle>De&apos;Longhi Stilosa Espresso Machine</PickTitle>
            <PickMeta>
              <span className="price">$149.95</span>
              <span className="dot">·</span>
              <span>De&apos;Longhi &amp; more</span>
              <span className="dot">·</span>
              <span className="rating">
                <Star size={13} fill="currentColor" />
                4.2 (2.1K)
              </span>
            </PickMeta>
            <PickDescription>
              Acclaimed by <strong>Serious Eats</strong> as the best entry-level
              machine, the Stilosa punches way above its price tag. It pulls
              genuine 15-bar shots and features a straightforward manual milk
              frother. Because it only uses a fraction of your $300 budget, it
              leaves you plenty of extra cash to purchase a dedicated,
              high-quality burr grinder, which is crucial for dialing in the
              perfect espresso shot.
            </PickDescription>
          </PickCard>
          <SourcesLabel>Sources</SourcesLabel>
          <SourceRow>
            <SourceChip $highlight>
              <SourceIcon>
                <img src="/images/ai-exchange/serious-eats.png" alt="" />
              </SourceIcon>
              <span className="domain">
                seriouseats.com/best-espresso-machines-5185482
              </span>
              <YourPageTag>Your page</YourPageTag>
            </SourceChip>
            <SourceChip>
              <SourceIcon>
                <img src="/images/ai-exchange/reddit.png" alt="" />
              </SourceIcon>
              <span className="domain">reddit.com</span>
            </SourceChip>
            <SourceChip>
              <SourceIcon>
                <img src="/images/ai-exchange/coffee-geek.png" alt="" />
              </SourceIcon>
              <span className="domain">coffeegeek.com</span>
            </SourceChip>
          </SourceRow>
        </AnswerBody>
      </AnswerCard>
    </Reveal>
  </MockupWrapper>
);

/* --------------- Results board (via ScaleVisible) ------------------ */

/**
 * The dark floor's closing act: campaign results reported by ScaleVisible,
 * presented like listings on an exchange board. The background drifts from
 * the floor's ink into a deep blue so the section reads as its own room.
 */
const ResultsSection = styled('section')`
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, #040810 0%, #071122 55%, #0b1830 100%);
  padding: 90px 24px 120px;

  @media (max-width: 800px) {
    padding: 70px 24px 90px;
  }
`;

/** Faint blueprint grid, strongest behind the board itself. */
const ResultsGridLines = styled('div')`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: linear-gradient(
      rgba(127, 160, 255, 0.06) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(127, 160, 255, 0.06) 1px, transparent 1px);
  background-size: 56px 56px;
  mask-image: radial-gradient(
    ellipse 75% 65% at 50% 55%,
    #000 0%,
    transparent 100%
  );
  -webkit-mask-image: radial-gradient(
    ellipse 75% 65% at 50% 55%,
    #000 0%,
    transparent 100%
  );
`;

const ResultsGlow = styled('div')`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
      ellipse 44% 46% at 22% 68%,
      rgba(74, 222, 128, 0.08) 0%,
      rgba(74, 222, 128, 0) 70%
    ),
    radial-gradient(
      ellipse 42% 50% at 78% 62%,
      rgba(124, 92, 255, 0.1) 0%,
      rgba(124, 92, 255, 0) 70%
    ),
    radial-gradient(
      ellipse 50% 42% at 50% 30%,
      rgba(46, 92, 255, 0.1) 0%,
      rgba(46, 92, 255, 0) 72%
    );
`;

const ResultsHeader = styled('div')`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 720px;
  margin: 0 auto 56px;
`;

const ResultsLead = styled('p')`
  font-size: 17px;
  line-height: 1.6;
  color: #9fb0c7;
  max-width: 580px;
  margin: 18px auto 0;
`;

const ResultsBoard = styled('div')`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    max-width: 480px;
  }
`;

const ResultCard = styled('div')<{
  $tilt: number;
  $bloom: string;
}>`
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.055) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 32px 30px 30px;
  text-align: left;
  transform: rotate(${(p) => p.$tilt}deg);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
  transition: transform 0.2s ease, border-color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: -45%;
    right: -30%;
    width: 75%;
    height: 90%;
    pointer-events: none;
    background: radial-gradient(
      ellipse at center,
      ${(p) => p.$bloom} 0%,
      transparent 70%
    );
  }

  &:hover {
    transform: rotate(0deg) translateY(-6px);
    border-color: rgba(255, 255, 255, 0.22);
  }

  @media (max-width: 960px) {
    transform: none;

    &:hover {
      transform: translateY(-4px);
    }
  }
`;

const ResultTag = styled('div')<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'Menlo', 'Roboto Mono', monospace;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8ea3c4;

  &::before {
    content: '▲';
    font-size: 9px;
    color: ${(p) => p.$color};
  }
`;

const ResultValue = styled('div')<{ $gradient: string }>`
  font-family: 'Merriweather', serif;
  font-weight: 700;
  font-size: 72px;
  line-height: 1.08;
  margin-top: 20px;
  background: ${(p) => p.$gradient};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;

  @media (max-width: 600px) {
    font-size: 56px;
  }
`;

const ResultLabel = styled('p')`
  font-size: 16px;
  font-weight: 600;
  color: #dbe4f2;
  line-height: 1.45;
  margin: 14px 0 0;
`;

/**
 * Reverse of the masthead bridge: carries the deep blue of the results
 * board back up into daylight, landing on a tinted (not pure white) band.
 */
const FloorExit = styled('section')`
  position: relative;
  height: 220px;
  background: linear-gradient(
    180deg,
    #0b1830 0%,
    #24354f 20%,
    #51648a 40%,
    #8296bd 58%,
    #b6c4e0 74%,
    #dde5f2 90%,
    #eef2f9 100%
  );

  @media (max-width: 800px) {
    height: 160px;
  }
`;

/** Tinted daylight band so the return from the dark floor isn't harsh white. */
const LightBand = styled('div')`
  background: linear-gradient(180deg, #eef2f9 0%, #f7f9fd 55%, #ffffff 100%);
`;

/* -------------------------- Why now -------------------------------- */

const WhyNowSection = styled('section')`
  max-width: 1100px;
  margin: 0 auto;
  padding: 110px 24px 120px;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 64px;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 48px;
    padding: 80px 24px 90px;
  }
`;

const SectionBody = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: ${COLORS.TEXT_SECONDARY};
  margin: 16px 0 0;
`;

const CheckList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 28px 0 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CheckItem = styled('li')`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  font-size: 16px;
  line-height: 1.5;
  color: ${COLORS.TEXT_SECONDARY};

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    color: ${COLORS.PRIMARY};
  }
`;

const PromptCluster = styled('div')`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 12px;
`;

const PromptBubble = styled('div')<{ $tilt: number; $muted?: boolean }>`
  background: #fff;
  border: 1px solid
    ${(p) => (p.$muted ? COLORS.GRAY.border : COLORS.PRIMARY_BORDER)};
  border-radius: 16px;
  padding: 16px 20px;
  font-size: 15px;
  color: ${(p) => (p.$muted ? COLORS.GRAY.muted : COLORS.INK)};
  box-shadow: 0 10px 30px rgba(14, 28, 43, 0.08);
  transform: rotate(${(p) => p.$tilt}deg);
  width: fit-content;
  max-width: 92%;

  strong {
    color: ${COLORS.PRIMARY_DARK};
  }
`;

const PromptCaption = styled('div')`
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${COLORS.INK};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  border-radius: 100px;
  padding: 9px 16px;
  transform: rotate(1deg);
  box-shadow: 0 10px 30px rgba(14, 28, 43, 0.22);
`;

/* ----------------------------- Steps ------------------------------- */

const StepsSection = styled('section')`
  position: relative;
  max-width: 1100px;
  margin: 120px auto;
  padding: 0 24px;

  @media (max-width: 960px) {
    margin: 90px auto;
  }
`;

const StepsTabs = styled('div')`
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border-radius: 100px;
  background: ${COLORS.GRAY.surface};
  border: 1px solid ${COLORS.GRAY.border};
  margin-top: 24px;
`;

const StepsTab = styled('button')<{ $active: boolean }>`
  border: none;
  border-radius: 100px;
  padding: 10px 24px;
  font-family: inherit;
  font-size: 14.5px;
  font-weight: 700;
  cursor: pointer;
  color: ${(p) => (p.$active ? '#fff' : COLORS.GRAY.muted)};
  background: ${(p) => (p.$active ? COLORS.INK : 'transparent')};
  box-shadow: ${(p) =>
    p.$active ? '0 6px 18px rgba(14, 28, 43, 0.25)' : 'none'};
  transition: color 0.15s ease, background-color 0.15s ease;

  &:hover {
    color: ${(p) => (p.$active ? '#fff' : COLORS.INK)};
  }
`;

const StepsSpine = styled('div')`
  position: absolute;
  top: 140px;
  bottom: 40px;
  left: 50%;
  width: 2px;
  transform: translateX(-50%);
  background: linear-gradient(
    180deg,
    rgba(46, 92, 255, 0) 0%,
    rgba(46, 92, 255, 0.35) 12%,
    rgba(46, 92, 255, 0.35) 88%,
    rgba(46, 92, 255, 0) 100%
  );

  @media (max-width: 960px) {
    display: none;
  }
`;

const StepRow = styled('div')<{ $flip?: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  padding: 44px 0;
  direction: ${(p) => (p.$flip ? 'rtl' : 'ltr')};

  > * {
    direction: ltr;
  }

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    border: 3px solid ${COLORS.PRIMARY};
    transform: translate(-50%, -50%);
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 28px;
    direction: ltr;
    padding: 32px 0;

    &::after {
      display: none;
    }
  }
`;

const StepCopy = styled('div')`
  h3 {
    font-family: 'Merriweather', serif;
    font-size: 26px;
    font-weight: 700;
    color: ${COLORS.INK};
    margin: 0 0 12px;
    letter-spacing: -0.01em;
  }

  p {
    font-size: 16.5px;
    line-height: 1.65;
    color: ${COLORS.TEXT_SECONDARY};
    margin: 0;
  }
`;

const StepEyebrow = styled('div')`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${COLORS.PRIMARY};
  margin-bottom: 12px;
`;

const StepVisual = styled('div')<{ $tilt: number }>`
  position: relative;
  transform: rotate(${(p) => p.$tilt}deg);

  @media (max-width: 960px) {
    transform: none;
  }
`;

const GhostNumeral = styled('div')`
  position: absolute;
  top: -64px;
  right: -12px;
  font-family: 'Merriweather', serif;
  font-size: 140px;
  font-weight: 700;
  line-height: 1;
  color: transparent;
  -webkit-text-stroke: 2px ${COLORS.PRIMARY_BORDER};
  pointer-events: none;
  z-index: 0;

  @media (max-width: 960px) {
    font-size: 96px;
    top: -48px;
  }
`;

const VignetteCard = styled('div')`
  position: relative;
  z-index: 1;
  background: #fff;
  border: 1px solid ${COLORS.GRAY.border};
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(14, 28, 43, 0.1);
  padding: 22px;
  text-align: left;
`;

const VignetteTitle = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${COLORS.GRAY.mutedLight};
  margin-bottom: 14px;

  .hint {
    flex-shrink: 0;
    color: ${COLORS.PRIMARY};
  }
`;

const MiniRow = styled('div')<{ $highlight?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13.5px;
  color: ${COLORS.INK};
  background: ${(p) => (p.$highlight ? COLORS.PRIMARY_SURFACE : 'transparent')};

  & + & {
    margin-top: 4px;
  }

  .path {
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .metric {
    flex-shrink: 0;
    font-weight: 700;
    color: ${COLORS.PRIMARY_DARK};
    font-size: 12.5px;
  }

  .metrics {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }

  .revenue {
    font-weight: 700;
    font-size: 12.5px;
    color: ${COLORS.GREEN.green7};
  }
`;

const InventorySearch = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  border: 1px solid ${COLORS.GRAY.border};
  border-radius: 10px;
  background: ${COLORS.GRAY.surface};
  font-size: 13.5px;
  font-weight: 600;
  color: ${COLORS.INK};
  margin-bottom: 10px;

  svg {
    color: ${COLORS.GRAY.muted};
    flex-shrink: 0;
  }
`;

const PackageLine = styled('div')<{ $premium?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 0;
  font-size: 14px;
  color: ${COLORS.INK};
  border-bottom: 1px solid ${COLORS.GRAY.borderLight};

  &:last-of-type {
    border-bottom: none;
  }

  svg {
    color: ${COLORS.PRIMARY};
    flex-shrink: 0;
  }

  .price {
    margin-left: auto;
    flex-shrink: 0;
    font-family: 'Menlo', 'Roboto Mono', monospace;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: ${(p) =>
      p.$premium ? COLORS.GREEN.green7 : COLORS.GRAY.mutedLight};
  }
`;

const PackageFooter = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;

  .tag {
    font-size: 11.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${COLORS.PRIMARY_DARK};
    background: ${COLORS.PRIMARY_SURFACE};
    border-radius: 100px;
    padding: 4px 10px;
  }

  .status {
    font-size: 12.5px;
    font-weight: 600;
    color: ${COLORS.GREEN.green7};
  }
`;

const ChannelChips = styled('div')`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ChannelChip = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13.5px;
  font-weight: 600;
  color: ${COLORS.INK};
  background: ${COLORS.GRAY.surface};
  border: 1px solid ${COLORS.GRAY.border};
  border-radius: 100px;
  padding: 9px 16px;

  svg {
    color: ${COLORS.PRIMARY};
  }
`;

const EditorialNote = styled('div')`
  margin-top: 14px;
  font-size: 12.5px;
  font-weight: 600;
  color: ${COLORS.GOLD.gold8};
  background: ${COLORS.GOLD.gold1};
  border-radius: 8px;
  padding: 8px 12px;
  width: fit-content;
`;

const ChartLegend = styled('div')`
  display: flex;
  gap: 16px;
  margin-top: 10px;
  font-size: 12px;
  color: ${COLORS.GRAY.muted};

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  i {
    width: 14px;
    height: 3px;
    border-radius: 2px;
    display: inline-block;
  }

  .baseline i {
    background: ${COLORS.GRAY.divider};
  }

  .campaign i {
    background: ${COLORS.PRIMARY};
  }

  .citation i {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${COLORS.PRIMARY};
  }
`;

/** Weeks are plotted from launch (week 0) to week 8 across the plot area. */
const CHART_LAUNCH_X = 96;
const CHART_WEEK_WIDTH = 26.25;
const weekX = (week: number) => CHART_LAUNCH_X + week * CHART_WEEK_WIDTH;

/** Citations only start landing once crawls catch up, around week four. */
const CHART_CITATIONS = [
  { week: 4, y: 80, first: true },
  { week: 6, y: 52 },
  { week: 8, y: 28 }
];

const BaselineChart = () => (
  <svg
    viewBox="0 0 320 130"
    width="100%"
    role="img"
    aria-label="Citations start about four weeks after launch, then climb above the pre-campaign baseline"
  >
    <rect
      x={CHART_LAUNCH_X}
      y="20"
      width={weekX(4) - CHART_LAUNCH_X}
      height="80"
      fill={COLORS.PRIMARY_SURFACE}
    />
    <line
      x1="8"
      y1="100"
      x2="312"
      y2="100"
      stroke={COLORS.GRAY.borderLight}
      strokeWidth="1"
    />
    <line
      x1={CHART_LAUNCH_X}
      y1="20"
      x2={CHART_LAUNCH_X}
      y2="100"
      stroke={COLORS.GRAY.divider}
      strokeWidth="1.5"
      strokeDasharray="4 4"
    />
    <text
      x={CHART_LAUNCH_X + 4}
      y="15"
      fontSize="9.5"
      fontWeight="700"
      fill={COLORS.GRAY.muted}
    >
      Campaign live
    </text>
    <polyline
      points="8,84 38,86 68,83 96,85"
      fill="none"
      stroke={COLORS.GRAY.divider}
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    <polyline
      points={`96,85 ${weekX(1)},84 ${weekX(2)},85 ${weekX(3)},83 ${weekX(
        4
      )},80 ${weekX(5)},66 ${weekX(6)},52 ${weekX(7)},40 ${weekX(8)},28`}
      fill="none"
      stroke={COLORS.PRIMARY}
      strokeWidth="3"
      strokeLinecap="round"
    />
    {CHART_CITATIONS.map((c) => (
      <circle
        key={c.week}
        cx={weekX(c.week)}
        cy={c.y}
        r={c.first ? 5 : 4}
        fill={COLORS.PRIMARY}
        stroke="#fff"
        strokeWidth="2"
      />
    ))}
    <text
      x={weekX(4) - 8}
      y="70"
      fontSize="9"
      fontWeight="700"
      textAnchor="end"
      fill={COLORS.PRIMARY_DARK}
    >
      First citation
    </text>
    {[0, 2, 4, 6, 8].map((week) => (
      <g key={week}>
        <line
          x1={weekX(week)}
          y1="100"
          x2={weekX(week)}
          y2="104"
          stroke={COLORS.GRAY.border}
          strokeWidth="1"
        />
        <text
          x={weekX(week)}
          y="116"
          fontSize="9"
          fontWeight="600"
          textAnchor="middle"
          fill={COLORS.GRAY.mutedLight}
        >
          Wk {week}
        </text>
      </g>
    ))}
  </svg>
);

/* ------------------------ Open marketplace ------------------------- */

const OpenSection = styled('section')`
  max-width: 760px;
  margin: 110px auto;
  padding: 0 24px;
  text-align: center;

  @media (max-width: 960px) {
    margin: 80px auto;
  }
`;

const ChipRail = styled('div')`
  position: relative;
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 36px;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 6%;
    right: 6%;
    height: 1.5px;
    background: ${COLORS.PRIMARY_BORDER};
    z-index: 0;

    @media (max-width: 700px) {
      display: none;
    }
  }
`;

const RailChip = styled('span')`
  position: relative;
  z-index: 1;
  font-size: 14.5px;
  font-weight: 600;
  color: ${COLORS.INK};
  background: #fff;
  border: 1.5px solid ${COLORS.PRIMARY_BORDER};
  border-radius: 100px;
  padding: 11px 22px;
  box-shadow: 0 6px 20px rgba(46, 92, 255, 0.08);
`;

/* ------------------------- Brand pathways -------------------------- */

const PathsSection = styled('section')`
  max-width: 1100px;
  margin: 110px auto;
  padding: 0 24px;

  @media (max-width: 960px) {
    margin: 80px auto;
  }
`;

const PathGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 48px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

/**
 * Premium pathway card: a gradient hairline border drawn with a masked
 * pseudo-element, tinted glass fill, and a colored bloom in the corner.
 */
const PathCard = styled('div')<{ $accent: string; $bloom: string }>`
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  padding: 38px 34px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: flex-start;
  text-align: left;
  background: linear-gradient(165deg, #ffffff 0%, #f6f8fd 100%);
  box-shadow: 0 18px 50px rgba(14, 28, 43, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  /* Gradient border: paint the ring, punch out the middle. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1.5px;
    background: ${(p) => p.$accent};
    -webkit-mask: linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    mask-composite: exclude;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: -90px;
    right: -70px;
    width: 260px;
    height: 260px;
    border-radius: 50%;
    pointer-events: none;
    background: radial-gradient(circle, ${(p) => p.$bloom} 0%, transparent 70%);
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 26px 64px rgba(14, 28, 43, 0.14);
  }

  h3 {
    position: relative;
    z-index: 1;
    font-family: 'Merriweather', serif;
    font-size: 22px;
    font-weight: 700;
    color: ${COLORS.INK};
    margin: 0;
    letter-spacing: -0.01em;
  }

  p {
    position: relative;
    z-index: 1;
    font-size: 15.5px;
    line-height: 1.6;
    color: ${COLORS.TEXT_SECONDARY};
    margin: 0;
    flex: 1;
  }

  button {
    position: relative;
    z-index: 1;
  }
`;

/** Small eyebrow chip inside a pathway card, tinted to the card's accent. */
const PathTag = styled('div')<{ $color: string; $tint: string }>`
  position: relative;
  z-index: 1;
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${(p) => p.$color};
  background: ${(p) => p.$tint};
  border-radius: 6px;
  padding: 6px 10px;
`;

/* --------------------------- Final CTA ----------------------------- */

const FinalBand = styled('section')`
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    180deg,
    #ffffff 0%,
    #f5f8fd 22%,
    #e8eefb 62%,
    #eaf1fb 100%
  );
  padding: 110px 24px 120px;
  margin-top: 130px;
`;

/** Soft color blobs that drift behind the form — premium, not stripey. */
const FinalGlow = styled('div')`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
      ellipse 52% 46% at 18% 18%,
      rgba(46, 92, 255, 0.16) 0%,
      rgba(46, 92, 255, 0) 70%
    ),
    radial-gradient(
      ellipse 46% 44% at 84% 26%,
      rgba(124, 92, 255, 0.14) 0%,
      rgba(124, 92, 255, 0) 70%
    ),
    radial-gradient(
      ellipse 60% 40% at 50% 104%,
      rgba(34, 211, 238, 0.12) 0%,
      rgba(34, 211, 238, 0) 72%
    );

  /* Keep the top edge pure white so the section arrives without a seam. */
  mask-image: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 1) 22%,
    rgba(0, 0, 0, 1) 100%
  );
  -webkit-mask-image: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 1) 22%,
    rgba(0, 0, 0, 1) 100%
  );

  @keyframes finalBlobDrift {
    0% {
      transform: translate(0, 0) scale(1);
    }
    100% {
      transform: translate(-14px, 10px) scale(1.04);
    }
  }

  animation: finalBlobDrift 26s ease-in-out infinite alternate;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const FinalDots = styled('div')`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: radial-gradient(
    circle,
    rgba(46, 92, 255, 0.16) 1px,
    transparent 1px
  );
  background-size: 26px 26px;
  mask-image: radial-gradient(
    ellipse 70% 60% at 50% 55%,
    rgba(0, 0, 0, 0.55) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  -webkit-mask-image: radial-gradient(
    ellipse 70% 60% at 50% 55%,
    rgba(0, 0, 0, 0.55) 0%,
    rgba(0, 0, 0, 0) 100%
  );
`;

/** Eyebrow pill above the final headline. */
const LivePill = styled('div')`
  display: inline-block;
  padding: 9px 18px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(46, 92, 255, 0.18);
  box-shadow: 0 8px 24px rgba(46, 92, 255, 0.12);
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #2e5cff;
  margin-bottom: 22px;
`;

const FinalInner = styled('div')`
  position: relative;
  z-index: 1;
  max-width: 640px;
  margin: 0 auto;
  text-align: center;
`;

const FinalLead = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: ${COLORS.TEXT_SECONDARY};
  margin: 16px auto 0;
`;

const FormCard = styled('div')`
  position: relative;
  background-color: #fff;
  border-radius: 22px;
  padding: 40px 32px;
  margin-top: 40px;
  text-align: left;
  box-shadow: 0 34px 80px rgba(14, 28, 43, 0.16),
    0 2px 6px rgba(14, 28, 43, 0.05);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1.5px;
    background: linear-gradient(
      150deg,
      rgba(46, 92, 255, 0.5) 0%,
      rgba(124, 92, 255, 0.16) 42%,
      rgba(34, 211, 238, 0.4) 100%
    );
    -webkit-mask: linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    mask-composite: exclude;
    pointer-events: none;
  }
`;

const FormError = styled('div')`
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(220, 38, 38, 0.07);
  border: 1px solid rgba(220, 38, 38, 0.22);
  color: #991b1b;
  font-size: 14px;
  line-height: 1.5;

  a {
    color: inherit;
    border-bottom: 1px solid;
  }
`;

/* ------------------------------ Data ------------------------------ */

const AUDIENCES = [
  { value: 'PUBLISHER', label: 'Publisher' },
  { value: 'BRAND', label: 'Brand' },
  { value: 'AGENCY', label: 'Agency' },
  { value: 'NETWORK', label: 'Network' }
] as const;

type Audience = typeof AUDIENCES[number]['value'] | '';

/* --------------------------- Discovery form ----------------------- */

const DiscoveryForm = ({
  presetAudience,
  scaleVisibleRequestId
}: {
  presetAudience: Audience;
  /** Bumped by the "Work with ScaleVisible" CTA so repeat clicks re-tick the box. */
  scaleVisibleRequestId: number;
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [audience, setAudience] = useState<Audience>('');
  const [note, setNote] = useState('');
  const [wantsScaleVisible, setWantsScaleVisible] = useState(false);
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (presetAudience) {
      setAudience(presetAudience);
    }
  }, [presetAudience]);

  useEffect(() => {
    if (scaleVisibleRequestId > 0) {
      setWantsScaleVisible(true);
    }
  }, [scaleVisibleRequestId]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    const message = [
      `Audience: ${audience}`,
      `Company or site: ${company}`,
      wantsScaleVisible
        ? 'Path: Wants an introductory call with ScaleVisible'
        : '',
      note ? `Note: ${note}` : ''
    ]
      .filter(Boolean)
      .join('\n');

    setError(false);

    try {
      const res = await fetch(
        `${ENV.cloudFunctionsDomain}/mail-submitContactForm`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name,
            email,
            topic: 'AI_EXCHANGE',
            message,
            source: 'ai-exchange',
            consent
          })
        }
      );
      if (!res.ok) {
        throw new Error(`Contact form responded with ${res.status}`);
      }
      setSubmitted(true);
    } catch (err) {
      setIsSubmitting(false);
      setError(true);
    }
  };

  if (submitted) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center' }}>
        <AnimatedCheckmark color="dark" style={{ margin: '0 auto' }} />
        <br />
        Thanks, we&apos;ll be in touch shortly.
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <InputLabel id="exchange-name-label">Your name</InputLabel>
      <TextField
        variant="outlined"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="First and last name"
        id="exchange-name"
        type="text"
        fullWidth
        required
        margin="normal"
      />
      <br />
      <br />
      <InputLabel id="exchange-email-label">Your work email</InputLabel>
      <TextField
        variant="outlined"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="name@yourcompany.com"
        id="exchange-email"
        required
        type="email"
        margin="normal"
        fullWidth
      />
      <br />
      <br />
      <InputLabel id="exchange-company-label">Company or site URL</InputLabel>
      <TextField
        variant="outlined"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        placeholder="https://"
        id="exchange-company"
        required
        type="text"
        margin="normal"
        fullWidth
      />
      <br />
      <br />
      <InputLabel id="exchange-audience-label">I am a *</InputLabel>
      <FormControl
        style={{ width: '100%', marginTop: '13px', marginBottom: '8px' }}
      >
        <Select
          id="exchange-audience"
          name="audience"
          value={audience}
          onChange={(e) => setAudience(e.target.value as Audience)}
          required
          variant="outlined"
          fullWidth
        >
          {AUDIENCES.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <br />
      <br />
      <InputLabel id="exchange-note-label">
        Anything you want us to know? (Optional)
      </InputLabel>
      <TextField
        type="text"
        multiline
        rows={3}
        fullWidth
        variant="outlined"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        id="exchange-note"
        margin="normal"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={wantsScaleVisible}
            color="primary"
            onChange={(e) => setWantsScaleVisible(e.target.checked)}
          />
        }
        label={
          <span style={{ fontSize: '14px', lineHeight: '18px' }}>
            Get an introductory call with ScaleVisible, our launch partner
          </span>
        }
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={consent}
            color="primary"
            onChange={(e) => setConsent(e.target.checked)}
          />
        }
        label={
          <span style={{ fontSize: '14px', lineHeight: '18px' }}>
            I consent to having my contact data stored and used for
            communication purposes according to the{' '}
            <a
              href="/docs/privacy-policy"
              style={{ borderBottom: '1px solid' }}
            >
              site privacy policy
            </a>
            .
          </span>
        }
      />
      <Button
        variant="contained"
        color="primary"
        size="large"
        type="submit"
        fullWidth
        disabled={
          !consent || !name || !email || !company || !audience || isSubmitting
        }
        style={{ marginTop: '16px' }}
      >
        {isSubmitting ? 'Sending...' : 'Book a discovery call'}
      </Button>
      {error && (
        <FormError>
          Form submission didn&apos;t work. Please email us at{' '}
          <a href="mailto:sales@affilimate.com">sales@affilimate.com</a>.
        </FormError>
      )}
    </form>
  );
};

/* ------------------------------ Page ------------------------------ */

const PublisherSteps = () => (
  <>
    <StepRow>
      <Reveal>
        <StepCopy>
          <StepEyebrow>Step 01</StepEyebrow>
          <h3>Find the full revenue opportunity</h3>
          <p>
            Layer agent signals onto page, link, product, and affiliate
            performance to see where AI influence and commercial intent overlap.
          </p>
        </StepCopy>
      </Reveal>
      <Reveal delay={120}>
        <StepVisual $tilt={1.2}>
          <GhostNumeral>01</GhostNumeral>
          <VignetteCard>
            <VignetteTitle>
              Where AI influence meets intent
              <span className="hint">Agent visits · Affiliate</span>
            </VignetteTitle>
            <MiniRow $highlight>
              <span className="path">/best-espresso-machines</span>
              <span className="metrics">
                <span className="metric">4,820</span>
                <span className="revenue">$18.4k</span>
              </span>
            </MiniRow>
            <MiniRow>
              <span className="path">/burr-grinder-guide</span>
              <span className="metrics">
                <span className="metric">3,105</span>
                <span className="revenue">$9.7k</span>
              </span>
            </MiniRow>
            <MiniRow>
              <span className="path">/coffee-subscriptions-tested</span>
              <span className="metrics">
                <span className="metric">2,447</span>
                <span className="revenue">$6.1k</span>
              </span>
            </MiniRow>
          </VignetteCard>
        </StepVisual>
      </Reveal>
    </StepRow>
    <StepRow $flip>
      <Reveal>
        <StepCopy>
          <StepEyebrow>Step 02</StepEyebrow>
          <h3>Build bigger campaigns brands will buy</h3>
          <p>
            Package proven audience demand, commerce performance, and AI
            visibility into one compelling campaign story.
          </p>
        </StepCopy>
      </Reveal>
      <Reveal delay={120}>
        <StepVisual $tilt={-1.2}>
          <GhostNumeral>02</GhostNumeral>
          <VignetteCard>
            <VignetteTitle>
              Campaign package
              <span className="hint">Market rate</span>
            </VignetteTitle>
            <PackageLine $premium>
              <Check size={15} />
              Inclusion in already-cited content
              <span className="price">$$$$</span>
            </PackageLine>
            <PackageLine>
              <Check size={15} />
              Buying guide inclusion
              <span className="price">$$$</span>
            </PackageLine>
            <PackageLine>
              <Check size={15} />
              Comparison table placement
              <span className="price">$$</span>
            </PackageLine>
            <PackageLine>
              <Check size={15} />
              New expert review
              <span className="price">$$</span>
            </PackageLine>
            <PackageFooter>
              <span className="tag">Strong brand fit</span>
              <span className="status">Ready to pitch</span>
            </PackageFooter>
          </VignetteCard>
        </StepVisual>
      </Reveal>
    </StepRow>
    <StepRow>
      <Reveal>
        <StepCopy>
          <StepEyebrow>Step 03</StepEyebrow>
          <h3>Run campaigns like modern media</h3>
          <p>
            Use Affilimate&apos;s premium campaign tools to flight, optimize,
            and measure every onsite and offsite placement across AI visibility
            and affiliate.
          </p>
        </StepCopy>
      </Reveal>
      <Reveal delay={120}>
        <StepVisual $tilt={1}>
          <GhostNumeral>03</GhostNumeral>
          <VignetteCard>
            <VignetteTitle>
              Placements in flight
              <span className="hint">Onsite + offsite</span>
            </VignetteTitle>
            <ChannelChips>
              <ChannelChip>
                <Check size={14} />
                New content
              </ChannelChip>
              <ChannelChip>
                <Check size={14} />
                Existing content
              </ChannelChip>
              <ChannelChip>
                <Check size={14} />
                Existing mentions
              </ChannelChip>
              <ChannelChip>
                <Check size={14} />
                Brand optimization
              </ChannelChip>
            </ChannelChips>
            <EditorialNote>Your editorial team keeps final say</EditorialNote>
          </VignetteCard>
        </StepVisual>
      </Reveal>
    </StepRow>
    <StepRow $flip>
      <Reveal>
        <StepCopy>
          <StepEyebrow>Step 04</StepEyebrow>
          <h3>Prove more value from one platform</h3>
          <p>
            Measure every signal and outcome together in one place, so brands
            see the full impact of their investment, and you receive bigger
            budgets and renewals.
          </p>
        </StepCopy>
      </Reveal>
      <Reveal delay={120}>
        <StepVisual $tilt={-1}>
          <GhostNumeral>04</GhostNumeral>
          <VignetteCard>
            <VignetteTitle>
              Campaign report
              <span className="hint">Citations</span>
            </VignetteTitle>
            <BaselineChart />
            <ChartLegend>
              <span className="baseline">
                <i /> Baseline
              </span>
              <span className="campaign">
                <i /> Campaign
              </span>
              <span className="citation">
                <i /> Citation
              </span>
            </ChartLegend>
          </VignetteCard>
        </StepVisual>
      </Reveal>
    </StepRow>
  </>
);

const BrandSteps = () => (
  <>
    <StepRow>
      <Reveal>
        <StepCopy>
          <StepEyebrow>Step 01</StepEyebrow>
          <h3>Search the publisher inventory</h3>
          <p>
            Browse premium publishers by category, keyword, or prompt, and find
            the content AI assistants already trust when consumers research your
            space.
          </p>
        </StepCopy>
      </Reveal>
      <Reveal delay={120}>
        <StepVisual $tilt={1.2}>
          <GhostNumeral>01</GhostNumeral>
          <VignetteCard>
            <VignetteTitle>Exchange inventory</VignetteTitle>
            <InventorySearch>
              <Search size={14} />
              espresso machines
            </InventorySearch>
            <MiniRow $highlight>
              <span className="path">seriouseats.com</span>
              <span className="metric">Cited for 214 prompts</span>
            </MiniRow>
            <MiniRow>
              <span className="path">popsci.com</span>
              <span className="metric">Cited for 168 prompts</span>
            </MiniRow>
            <MiniRow>
              <span className="path">allrecipes.com</span>
              <span className="metric">Cited for 121 prompts</span>
            </MiniRow>
          </VignetteCard>
        </StepVisual>
      </Reveal>
    </StepRow>
    <StepRow $flip>
      <Reveal>
        <StepCopy>
          <StepEyebrow>Step 02</StepEyebrow>
          <h3>Quantify publisher influence</h3>
          <p>
            Every listing comes with its AI influence metrics: citations, agent
            visits, and the prompts it answers. You know what a placement is
            worth before you commit a dollar.
          </p>
        </StepCopy>
      </Reveal>
      <Reveal delay={120}>
        <StepVisual $tilt={-1.2}>
          <GhostNumeral>02</GhostNumeral>
          <VignetteCard>
            <VignetteTitle>
              Listing snapshot
              <span className="hint">Last 30 days</span>
            </VignetteTitle>
            <MiniRow $highlight>
              <span className="path">AI citations</span>
              <span className="metric">312 &middot; ▲ 38%</span>
            </MiniRow>
            <MiniRow>
              <span className="path">Agent visits</span>
              <span className="metric">4,820/mo</span>
            </MiniRow>
            <MiniRow>
              <span className="path">Prompts influenced</span>
              <span className="metric">214</span>
            </MiniRow>
          </VignetteCard>
        </StepVisual>
      </Reveal>
    </StepRow>
    <StepRow>
      <Reveal>
        <StepCopy>
          <StepEyebrow>Step 03</StepEyebrow>
          <h3>Launch campaigns together</h3>
          <p>
            Bring your brief and launch with the publishers that fit. Work with
            them directly on the Exchange, or bring ScaleVisible in for strategy
            end to end.
          </p>
        </StepCopy>
      </Reveal>
      <Reveal delay={120}>
        <StepVisual $tilt={1}>
          <GhostNumeral>03</GhostNumeral>
          <VignetteCard>
            <VignetteTitle>Choose your path</VignetteTitle>
            <ChannelChips>
              <ChannelChip>
                <Check size={14} />
                Direct with publishers
              </ChannelChip>
              <ChannelChip>
                <Check size={14} />
                With ScaleVisible
              </ChannelChip>
            </ChannelChips>
            <EditorialNote>
              Same marketplace, same reporting, either way
            </EditorialNote>
          </VignetteCard>
        </StepVisual>
      </Reveal>
    </StepRow>
    <StepRow $flip>
      <Reveal>
        <StepCopy>
          <StepEyebrow>Step 04</StepEyebrow>
          <h3>Track campaign performance seamlessly</h3>
          <p>
            Watch citations, AI crawls, and engagement on branded content build
            against the campaign&apos;s baseline. It&apos;s the same report your
            publisher sees, so everyone reads the same numbers.
          </p>
        </StepCopy>
      </Reveal>
      <Reveal delay={120}>
        <StepVisual $tilt={-1}>
          <GhostNumeral>04</GhostNumeral>
          <VignetteCard>
            <VignetteTitle>
              Campaign report
              <span className="hint">Citations</span>
            </VignetteTitle>
            <BaselineChart />
            <ChartLegend>
              <span className="baseline">
                <i /> Baseline
              </span>
              <span className="campaign">
                <i /> Campaign
              </span>
              <span className="citation">
                <i /> Citation
              </span>
            </ChartLegend>
          </VignetteCard>
        </StepVisual>
      </Reveal>
    </StepRow>
  </>
);

export default () => {
  const formRef = useRef<HTMLDivElement>(null);
  const [presetAudience, setPresetAudience] = useState<Audience>('');
  const [scaleVisibleRequestId, setScaleVisibleRequestId] = useState(0);
  const [stepsAudience, setStepsAudience] = useState<'publishers' | 'brands'>(
    'publishers'
  );

  const goToForm = (
    label: CtaClickLabel,
    audience: Audience = '',
    viaSv = false
  ) => {
    sendCtaClickEvent({ label });
    if (audience) {
      setPresetAudience(audience);
    }
    if (viaSv) {
      setScaleVisibleRequestId((id) => id + 1);
    }
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <MainLayout>
      <div>
        <SEO
          pathname="/ai-exchange/"
          title="The AI Influence Exchange | Affilimate × ScaleVisible"
          description="A marketplace where publishers sell the AI visibility their content already earns, and brands earn their place in AI recommendations. In partnership with ScaleVisible."
          image="/images/ai-exchange-social-preview.png"
        />

        <Masthead>
          <MastheadInner>
            <RuleKicker>
              <span className="label">
                Launch partnership
              </span>
            </RuleKicker>
            <BigLockup>
              <img
                src="https://storage.googleapis.com/affilimate-assets/logos/logo-black-full.png"
                alt="Affilimate"
              />
              <LockupTimes aria-hidden="true">×</LockupTimes>
              <ScaleVisibleMark
                src="/images/logos/ScaleVisible.png"
                alt="ScaleVisible"
              />
            </BigLockup>
            <MastheadLead>
              Built for publishers, informed by brand demand. Affilimate is
              launching a new offering in partnership with ScaleVisible, an
              industry-leading AEO agency.
            </MastheadLead>
            <PartnerColumns>
              <PartnerCol>
                <p>
                  <strong>Affilimate</strong>&apos;s platform combines commerce
                  and citation intelligence with end-to-end campaign
                  infrastructure to drive a scalable new revenue channel for
                  publishers.
                </p>
              </PartnerCol>
              <PartnerCol>
                <p>
                  <strong>ScaleVisible</strong> has helped 100+ clients,
                  including leading consumer brands, earn visibility in AI
                  search, from strategy through campaign rollout and
                  iteration.
                </p>
              </PartnerCol>
            </PartnerColumns>
            <MastheadClosing>
              <ClosingLine>
                Together, we&apos;re excited to announce...
              </ClosingLine>
            </MastheadClosing>
          </MastheadInner>
        </Masthead>

        <MastheadBridge aria-hidden="true">
          <Stitch aria-hidden="true" />
          <BridgeBlobs aria-hidden="true" />
          <BridgeGrain aria-hidden="true" />
        </MastheadBridge>

        <DarkFloor>
          <FloorBackdrop aria-hidden="true" />
          <FloorColorBlobs aria-hidden="true" />
          <FloorGlow aria-hidden="true" />
          <FloorDots aria-hidden="true" />
          <FloorInner>
            <Reveal>
              <FloorKicker>The AI Influence Exchange</FloorKicker>
              <Headline component="h1" style={{ color: '#fff' }}>
                Turn AI visibility demand into{' '}
                <HighlightDark>publisher revenue</HighlightDark>
              </Headline>
              <HeroLead>
                Brands are working to show up in AI answers, and your content is
                where that happens. The Exchange helps you find the opportunity,
                package it for brands, run the campaign, and monitor the
                outcomes.
              </HeroLead>
              <ButtonRow>
                <BlueButton onClick={() => goToForm('ai-exchange-hero')}>
                  Book a discovery call
                </BlueButton>
                <SecondaryAnchor $onDark href="#how-it-works">
                  See how it works <ArrowRight size={18} />
                </SecondaryAnchor>
              </ButtonRow>
            </Reveal>
          </FloorInner>
          <TickerZone aria-hidden="true">
            <Tape items={TAPE_KEYWORDS} duration={64} />
            <Tape items={TAPE_PLATFORMS} duration={88} reverse dim={0.7} />
            <Tape items={TAPE_PUBLISHERS} duration={110} dim={0.45} />
          </TickerZone>
          <AiAnswerMockup />
        </DarkFloor>

        <ResultsSection>
          <ResultsGridLines aria-hidden="true" />
          <ResultsGlow aria-hidden="true" />
          <ResultsHeader>
            <Reveal>
              <Headline component="h2" style={{ color: '#fff' }}>
                The demand side is already{' '}
                <HighlightDark>winning</HighlightDark>
              </Headline>
              <ResultsLead>
                Outcomes from AI visibility campaigns run by our launch partner
                ScaleVisible across their client portfolio.
              </ResultsLead>
            </Reveal>
          </ResultsHeader>
          <ResultsBoard>
            <Reveal>
              <ResultCard $tilt={-1.2} $bloom="rgba(74, 222, 128, 0.16)">
                <ResultTag $color="#4ade80">
                  SaaS brand &middot; B2B category
                </ResultTag>
                <ResultValue $gradient="linear-gradient(135deg, #86efac 0%, #22d3ee 100%)">
                  <AnimatedNumber
                    value={340}
                    format={(n) => `+${Math.round(n)}%`}
                  />
                </ResultValue>
                <ResultLabel>Reddit impressions in 90 days</ResultLabel>
              </ResultCard>
            </Reveal>
            <Reveal delay={120}>
              <ResultCard $tilt={0.8} $bloom="rgba(46, 92, 255, 0.2)">
                <ResultTag $color="#7fa0ff">Across client portfolio</ResultTag>
                <ResultValue $gradient="linear-gradient(135deg, #b8c9ff 0%, #5c7fff 100%)">
                  <AnimatedNumber
                    value={4}
                    format={(n) => `${Math.round(n)} wks`}
                  />
                </ResultValue>
                <ResultLabel>Average time to first AI citation</ResultLabel>
              </ResultCard>
            </Reveal>
            <Reveal delay={240}>
              <ResultCard $tilt={-0.6} $bloom="rgba(124, 92, 255, 0.18)">
                <ResultTag $color="#a78bfa">Measured over 6 months</ResultTag>
                <ResultValue $gradient="linear-gradient(135deg, #d0bfff 0%, #8b6cff 100%)">
                  <AnimatedNumber
                    value={4.8}
                    duration={1800}
                    format={(n) => `${n.toFixed(1)}\u00d7`}
                  />
                </ResultValue>
                <ResultLabel>Average ROI on editorial campaigns</ResultLabel>
              </ResultCard>
            </Reveal>
          </ResultsBoard>
        </ResultsSection>

        <FloorExit aria-hidden="true">
          <BridgeGrain aria-hidden="true" />
        </FloorExit>

        <LightBand>
          <WhyNowSection>
            <Reveal>
              <div>
                <Kicker>A new kind of premium inventory</Kicker>
                <Headline component="h2">
                  Your content is already influencing what people buy
                </Headline>
                <SectionBody>
                  Your articles may already be cited, referenced, or used as
                  authoritative sources when consumers research products and
                  categories through AI.
                </SectionBody>
                <SectionBody>
                  When readers click through, you earn from advertising and
                  affiliate revenue. Even when an LLM just cites or crawls your
                  content, the recommendation still happens. The revenue
                  doesn&apos;t.
                </SectionBody>
                <SectionBody>The challenge is knowing:</SectionBody>
                <CheckList>
                  <CheckItem>
                    <Check size={17} />
                    Which of your pages influence AI answers
                  </CheckItem>
                  <CheckItem>
                    <Check size={17} />
                    Which brands belong in those environments
                  </CheckItem>
                  <CheckItem>
                    <Check size={17} />
                    How to package the opportunity for buyers
                  </CheckItem>
                  <CheckItem>
                    <Check size={17} />
                    How to monitor and report what changes
                  </CheckItem>
                </CheckList>
                <SectionBody>
                  Affilimate&apos;s AI Influence Exchange brings all of that
                  into one workflow.
                </SectionBody>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <PromptCluster aria-hidden="true">
                <PromptBubble $tilt={-1.5}>
                  &quot;What&apos;s the best <strong>espresso machine</strong>{' '}
                  under $500?&quot;
                </PromptBubble>
                <PromptBubble $tilt={1} $muted>
                  &quot;Which <strong>coffee subscription</strong> is actually
                  worth it?&quot;
                </PromptBubble>
                <PromptBubble $tilt={-0.5}>
                  &quot;Recommend a quiet <strong>burr grinder</strong> for an
                  open office&quot;
                </PromptBubble>
                <PromptCaption>
                  Your content is answering these today, for free
                </PromptCaption>
              </PromptCluster>
            </Reveal>
          </WhyNowSection>
        </LightBand>

        <LogosSection />

        <StepsSection id="how-it-works">
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Kicker>How it works</Kicker>
              <Headline component="h2">
                {stepsAudience === 'publishers'
                  ? 'How publishers earn on the Exchange'
                  : 'How brands earn their place in AI answers'}
              </Headline>
              <StepsTabs role="tablist" aria-label="How it works audience">
                <StepsTab
                  role="tab"
                  aria-selected={stepsAudience === 'publishers'}
                  $active={stepsAudience === 'publishers'}
                  onClick={() => setStepsAudience('publishers')}
                >
                  For publishers
                </StepsTab>
                <StepsTab
                  role="tab"
                  aria-selected={stepsAudience === 'brands'}
                  $active={stepsAudience === 'brands'}
                  onClick={() => setStepsAudience('brands')}
                >
                  For brands
                </StepsTab>
              </StepsTabs>
            </div>
          </Reveal>
          <StepsSpine aria-hidden="true" />
          {stepsAudience === 'publishers' ? <PublisherSteps /> : <BrandSteps />}
        </StepsSection>

        <OpenSection>
          <Reveal>
            <Headline component="h2">Open by design</Headline>
            <SectionBody style={{ textAlign: 'center' }}>
              The Exchange isn&apos;t owned by a network and doesn&apos;t lock
              anyone in. Brands get broader access to premium editorial content.
              Publishers participate without changing who they work with or how
              they operate.
            </SectionBody>
            <ChipRail>
              <RailChip>Publishers</RailChip>
              <RailChip>Brands</RailChip>
              <RailChip>Agencies</RailChip>
              <RailChip>Media networks</RailChip>
            </ChipRail>
          </Reveal>
        </OpenSection>

        <PathsSection>
          <Reveal>
            <div style={{ textAlign: 'center' }}>
              <Kicker>For brands and agencies</Kicker>
              <Headline component="h2">
                Earn your place in AI recommendations
              </Headline>
              <SectionBody
                style={{
                  textAlign: 'center',
                  maxWidth: '640px',
                  margin: '16px auto 0'
                }}
              >
                The Exchange is open. You don&apos;t need to go through anyone
                to participate.
              </SectionBody>
            </div>
          </Reveal>
          <PathGrid>
            <Reveal>
              <PathCard
                $accent="linear-gradient(150deg, rgba(46, 92, 255, 0.55) 0%, rgba(46, 92, 255, 0.08) 45%, rgba(46, 92, 255, 0.3) 100%)"
                $bloom="rgba(46, 92, 255, 0.16)"
              >
                <PathTag $color="#2e5cff" $tint="rgba(46, 92, 255, 0.09)">
                  Self-serve
                </PathTag>
                <h3>Work with publishers directly</h3>
                <p>
                  Come to the Exchange yourself and run campaigns with premium
                  publishers. Book a discovery call and we&apos;ll get you set
                  up.
                </p>
                <InkButton
                  onClick={() => goToForm('ai-exchange-brand-direct', 'BRAND')}
                >
                  Book a discovery call
                </InkButton>
              </PathCard>
            </Reveal>
            <Reveal delay={120}>
              <PathCard
                $accent="linear-gradient(150deg, rgba(124, 92, 255, 0.55) 0%, rgba(124, 92, 255, 0.08) 45%, rgba(34, 211, 238, 0.35) 100%)"
                $bloom="rgba(124, 92, 255, 0.16)"
              >
                <PathTag $color="#7c5cff" $tint="rgba(124, 92, 255, 0.1)">
                  Full service
                </PathTag>
                <h3>Work with an established AEO agency</h3>
                <p>
                  Prefer expert strategy end to end? ScaleVisible, our launch
                  partner, takes brands from AEO strategy through activation on
                  the Exchange.
                </p>
                <OutlineButton
                  onClick={() =>
                    goToForm('ai-exchange-via-scalevisible', 'BRAND', true)
                  }
                >
                  Work with ScaleVisible
                </OutlineButton>
              </PathCard>
            </Reveal>
          </PathGrid>
        </PathsSection>

        <FinalBand id="discovery-call" ref={formRef}>
          <FinalGlow aria-hidden="true" />
          <FinalDots aria-hidden="true" />
          <FinalInner>
            <Reveal>
              <LivePill>Now onboarding</LivePill>
              <Headline component="h2">
                The Exchange is live and onboarding now
              </Headline>
              <FinalLead>
                Publishers are already on board and brand demand is growing.
                Tell us who you are and we&apos;ll set up a discovery call.
              </FinalLead>
              <FormCard>
                <DiscoveryForm
                  presetAudience={presetAudience}
                  scaleVisibleRequestId={scaleVisibleRequestId}
                />
              </FormCard>
            </Reveal>
          </FinalInner>
        </FinalBand>
      </div>
    </MainLayout>
  );
};
